import {
	createFileRoute,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { TrainingAssetsTable } from "@/components/tables/training-assets";
import { useEffect, useState, useTransition } from "react";
import type { fontesDeDadosQuery } from "./__generated__/fontesDeDadosQuery.graphql";
import type { fontesDeDados_assets$key } from "./__generated__/fontesDeDados_assets.graphql";
import type { fontesDeDadosPaginationQuery } from "./__generated__/fontesDeDadosPaginationQuery.graphql";
import { zodValidator } from "@tanstack/zod-adapter";
import z from "zod";
import { Field } from "@/components/ui/field";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { Icon } from "@/components/icon";
import { useDebounce } from "@uidotdev/usehooks";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/ia/fontes-de-dados",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			q: z.string().optional(),
		}),
	),
});

const PAGE_SIZE = 15;

function TrainingAssetsContent({
	dataRef,
}: {
	dataRef: fontesDeDados_assets$key;
}) {
	const [isPending, startTransition] = useTransition();

	const { data, hasNext, hasPrevious, loadNext, loadPrevious } =
		usePaginationFragment<
			fontesDeDadosPaginationQuery,
			fontesDeDados_assets$key
		>(
			graphql`
				fragment fontesDeDados_assets on Club
				@argumentDefinitions(
					first: { type: "Int", defaultValue: 15 }
					after: { type: "String" }
					last: { type: "Int" }
					before: { type: "String" }
					query: { type: "String" }
				)
				@refetchable(queryName: "fontesDeDadosPaginationQuery") {
					aiPersona {
						training {
							assets(first: $first, after: $after, last: $last, before: $before, query: $query)
								@connection(key: "fontesDeDados_assets") {
								edges {
									node {
										id
										rawID
										enabled
										status
										createdAt
										createdBy {
											displayName
										}
										asset {
											... on ScarfVideoTrainingAsset {
												file {
													name
													... on VideoFile {
														lessons {
															name
														}
													}
												}
											}
											... on RegularFileTrainingAsset {
												file {
													name
												}
											}
										}
									}
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
									startCursor
									endCursor
								}
							}
						}
					}
				}
			`,
			dataRef,
		);

	const assets =
		data.aiPersona?.training.assets.edges.map((edge) => edge.node) || [];
	const totalCount = assets.length;

	const handleLoadNext = () => {
		startTransition(() => {
			loadNext(PAGE_SIZE);
		});
	};

	const handleLoadPrevious = () => {
		startTransition(() => {
			loadPrevious(PAGE_SIZE);
		});
	};

	return (
		<TrainingAssetsTable
			data={assets}
			isPending={isPending}
			hasNextPage={hasNext}
			hasPreviousPage={hasPrevious}
			totalCount={totalCount}
			startIndex={totalCount > 0 ? 1 : 0}
			endIndex={totalCount}
			loadNext={handleLoadNext}
			loadPrevious={handleLoadPrevious}
		/>
	);
}

function RouteComponent() {
	const { q: query } = useSearch({ from: Route.id });
	const { club } = useRouteContext({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const [_isPending, startTransition] = useTransition();
	const [pendingQuery, setPendingQuery] = useState(query || "");
	const debouncedQuery = useDebounce(pendingQuery, 800);

	const data = useLazyLoadQuery<fontesDeDadosQuery>(
		graphql`
			query fontesDeDadosQuery($slug: String!, $query: String) {
				club(slug: $slug) {
					id
					...fontesDeDados_assets @arguments(query: $query)
				}
			}
		`,
		{ slug: club.slug, query: query?.length ? query : null },
	);

	useEffect(() => {
		startTransition(() => {
			navigate({
				search: (s) => ({
					...s,
					q: debouncedQuery,
				}),
			});
		});
	}, [debouncedQuery, navigate]);

	return (
		<div className="flex flex-col p-6">
			<div className="mb-6 max-w-md">
				<Field>
					<InputGroup>
						<InputGroupAddon>
							<Icon name="Search" className="size-4" />
						</InputGroupAddon>

						<InputGroupInput
							placeholder="Filtrar"
							value={pendingQuery}
							onChange={(e) => setPendingQuery(e.target.value)}
						/>
					</InputGroup>
				</Field>
			</div>

			<TrainingAssetsContent dataRef={data.club} />
		</div>
	);
}
